/**
 * Every open signal, across the whole scanned universe.
 *
 * The engine scans all 102 Vietnamese tickers but the watchlist holds ten,
 * and until this panel existed the other ninety-two were scanned and thrown
 * away at the last step: the dispatcher suppressed the alert ("symbol is not
 * on any watchlist") and the dashboard only ever showed the selected
 * symbol's signals. Seven live ideas were sitting in the database with no
 * way to see them.
 *
 * The watchlist keeps its meaning -- the things you follow, and the only
 * things Telegram will interrupt you about. This is the wider net, and the
 * plus button is how something moves from one to the other.
 */

import { useMemo } from 'react'

import type { Signal, Symbol } from '@/api/types'
import type { TickerSignal } from '@/components/filters/assetFilter'
import {
  useAddToWatchlist, useMarketSignals, useSymbols, useWatchlists,
} from '@/hooks/useQueries'
import { useAppStore } from '@/store/useAppStore'
import { formatPrice } from '@/components/Chart/ChartPanel'
import {
  ASSET_LABELS,
  ASSET_ORDER,
  AssetTabs,
  Highlight,
  SignalDot,
  assetCounts,
  matchesQuery,
  normalize,
  topSignalByTicker,
} from '@/components/filters/assetFilter'

export function MarketSignals() {
  const { data: signals, isLoading, error } = useMarketSignals()
  const { data: watchlists } = useWatchlists()
  const { data: allSymbols } = useSymbols()
  const selectTicker = useAppStore((s) => s.selectTicker)
  const add = useAddToWatchlist()

  // The same two controls as the watchlist, reading the same store. Two
  // panels filtered differently at the same time is how a reader concludes
  // a signal went missing.
  const assetTab = useAppStore((s) => s.assetTab)
  const setAssetTab = useAppStore((s) => s.setAssetTab)
  const query = useAppStore((s) => s.symbolQuery)
  const setQuery = useAppStore((s) => s.setSymbolQuery)
  const needle = normalize(query.trim())

  // Counts here are of signals, not of symbols: on this tab the number that
  // matters is how many ideas are open in each market, and "CK VN 3" beside
  // an empty crypto tab is the answer to "where is anything happening".
  const counts = useMemo(
    () => assetCounts((signals ?? []).map((s) => ({ asset_class: s.asset_class }))),
    [signals],
  )

  const symbolFor = useMemo(() => {
    const map = new Map<string, Symbol>()
    for (const symbol of allSymbols ?? []) map.set(symbol.ticker, symbol)
    return map
  }, [allSymbols])

  const watched = useMemo(
    () => new Set((watchlists ?? []).flatMap((w) => w.items.map((i) => i.symbol.ticker))),
    [watchlists],
  )
  const decimalsFor = useMemo(() => {
    const map = new Map<string, number>()
    for (const symbol of allSymbols ?? []) map.set(symbol.ticker, symbol.price_decimals ?? 2)
    return map
  }, [allSymbols])

  if (isLoading) return <p className="p-3 text-2xs text-ink-dim">Đang tải…</p>
  if (error) return <p className="p-3 text-2xs text-down">Không tải được danh sách tín hiệu</p>

  const all = [...(signals ?? [])].sort((a, b) => b.confidence - a.confidence)
  const rows = all.filter((signal) => {
    if (assetTab !== 'all' && signal.asset_class !== assetTab) return false
    if (!needle) return true
    const symbol = symbolFor.get(signal.ticker)
    return matchesQuery(
      symbol ?? {
        ticker: signal.ticker,
        display_name: null,
        asset_class: signal.asset_class,
      },
      needle,
    )
  })

  const offWatchlist = rows.filter((s) => !watched.has(s.ticker)).length
  const byTicker = topSignalByTicker(signals)

  // Grouped by market, strongest first inside each. A flat list ordered on
  // confidence alone interleaves a 60.4 Vietnamese scalp between two crypto
  // ideas, which reads as one undifferentiated pile.
  const grouped = ASSET_ORDER
    .map((assetClass) => [assetClass, rows.filter((r) => r.asset_class === assetClass)] as const)
    .filter(([, group]) => group.length > 0)

  return (
    <div className="p-2">
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <AssetTabs value={assetTab} counts={counts} onChange={setAssetTab} />
        <input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Lọc theo mã…"
          className="ml-auto w-40 rounded border border-border bg-panel px-2 py-0.5 text-2xs
                     text-ink placeholder:text-ink-dim focus:border-accent focus:outline-none"
        />
      </div>

      {all.length === 0 && (
        <p className="py-2 text-2xs text-ink-dim">
          Chưa có tín hiệu nào đang mở trên toàn bộ danh mục quét.
        </p>
      )}

      {all.length > 0 && rows.length === 0 && (
        <p className="py-2 text-2xs text-ink-dim">
          {all.length} tín hiệu đang mở, nhưng không có tín hiệu nào trong bộ lọc hiện tại.
        </p>
      )}

      {offWatchlist > 0 && (
        <p className="mb-2 rounded border border-accent/40 bg-accent/10 px-2 py-1 text-2xs text-ink">
          {offWatchlist}/{rows.length} tín hiệu ở mã ngoài danh mục theo dõi — Telegram
          không gửi cảnh báo cho những mã này. Bấm <b>+</b> để thêm.
        </p>
      )}
      {rows.length > 0 && (
      <table className="w-full text-2xs">
        <thead>
          <tr className="text-ink-dim">
            <th className="px-2 py-1 text-left font-medium">Mã</th>
            <th className="px-2 py-1 text-left font-medium">Khung</th>
            <th className="px-2 py-1 text-left font-medium">Chiều</th>
            <th className="px-2 py-1 text-right font-medium">Tin cậy</th>
            <th className="px-2 py-1 text-right font-medium">R:R</th>
            <th className="px-2 py-1 text-right font-medium">Vào lệnh</th>
            <th className="px-2 py-1 text-right font-medium">Cắt lỗ</th>
            <th className="px-2 py-1 text-center font-medium">Theo dõi</th>
          </tr>
        </thead>
        {grouped.map(([assetClass, group]) => (
        <tbody key={assetClass}>
          {/* The header is dropped when a single market is selected: the tab
              above already says which one. */}
          {assetTab === 'all' && (
            <tr>
              <td colSpan={8} className="bg-panel-hover px-2 py-1 text-2xs text-ink-dim">
                {ASSET_LABELS[assetClass]}
                <span className="ml-1 opacity-60">{group.length}</span>
              </td>
            </tr>
          )}
          {group.map((signal) => (
            <Row
              key={signal.id}
              signal={signal}
              needle={needle}
              badge={byTicker.get(signal.ticker)}
              decimals={decimalsFor.get(signal.ticker)}
              watched={watched.has(signal.ticker)}
              onSelect={() => selectTicker(signal.ticker)}
              onAdd={() => {
                const target =
                  watchlists?.find((w) => w.asset_class === signal.asset_class) ?? watchlists?.[0]
                if (target) add.mutate({ watchlistId: target.id, ticker: signal.ticker })
              }}
            />
          ))}
        </tbody>
        ))}
      </table>
      )}
    </div>
  )
}

function Row({
  signal, needle, badge, decimals, watched, onSelect, onAdd,
}: {
  signal: Signal
  needle: string
  badge: TickerSignal | undefined
  decimals?: number
  watched: boolean
  onSelect: () => void
  onAdd: () => void
}) {
  const long = signal.side === 'long'
  return (
    <tr className="border-t border-border/60 hover:bg-panel-hover">
      <td className="px-2 py-1">
        <button onClick={onSelect} className="text-ink hover:text-accent">
          <Highlight text={signal.ticker} needle={needle} />
        </button>
        <SignalDot signal={badge} />
      </td>
      <td className="px-2 py-1 text-ink-dim">{signal.timeframe} · {signal.horizon}</td>
      <td className={`px-2 py-1 ${long ? 'text-up' : 'text-down'}`}>
        {long ? 'MUA' : 'BÁN'}
      </td>
      <td className="px-2 py-1 text-right font-mono text-ink">{signal.confidence.toFixed(0)}</td>
      <td className="px-2 py-1 text-right font-mono text-ink-dim">
        {signal.rr_ratio.toFixed(2)}
      </td>
      <td className="px-2 py-1 text-right font-mono text-ink-dim">
        {formatPrice(signal.entry_ref, decimals)}
      </td>
      <td className="px-2 py-1 text-right font-mono text-ink-dim">
        {formatPrice(signal.stop_loss, decimals)}
      </td>
      <td className="px-2 py-1 text-center">
        {watched ? (
          <span className="text-ink-dim" title="Đã theo dõi">✓</span>
        ) : (
          <button
            onClick={onAdd}
            title="Thêm vào danh mục theo dõi để nhận cảnh báo Telegram"
            className="rounded px-1.5 text-accent hover:bg-accent/20"
          >
            +
          </button>
        )}
      </td>
    </tr>
  )
}
